/**
 * Role utility functions for user permission checking
 */

// Role constants
export const ROLES = {
  STUDENT: 'student',
  ADMIN: 'admin',
  SUPERADMIN: 'superadmin'
};

// Role hierarchy (higher number = more permissions)
const ROLE_HIERARCHY = {
  [ROLES.STUDENT]: 1,
  [ROLES.ADMIN]: 2,
  [ROLES.SUPERADMIN]: 3
};

/**
 * Check if user has a specific role
 * @param {string} userRole - Current user's role
 * @param {string} requiredRole - Required role to check against
 * @returns {boolean}
 */
export const hasRole = (userRole, requiredRole) => {
  if (!userRole || !requiredRole) return false;
  return userRole === requiredRole;
};

/**
 * Check if user has at least the specified role level (includes higher roles)
 * @param {string} userRole - Current user's role
 * @param {string} minimumRole - Minimum required role
 * @returns {boolean}
 */
export const hasMinimumRole = (userRole, minimumRole) => {
  if (!userRole || !minimumRole) return false;
  
  const userLevel = ROLE_HIERARCHY[userRole] || 0;
  const requiredLevel = ROLE_HIERARCHY[minimumRole] || 0;
  
  return userLevel >= requiredLevel;
};

/**
 * Check if user is a student
 * @param {string} userRole - Current user's role
 * @returns {boolean}
 */
export const isStudent = (userRole) => {
  return hasRole(userRole, ROLES.STUDENT);
};

/**
 * Check if user is an admin (admin or superadmin)
 * @param {string} userRole - Current user's role
 * @returns {boolean}
 */
export const isAdmin = (userRole) => {
  return hasMinimumRole(userRole, ROLES.ADMIN);
};

/**
 * Check if user is specifically an admin (not superadmin)
 * @param {string} userRole - Current user's role
 * @returns {boolean}
 */
export const isAdminOnly = (userRole) => {
  return hasRole(userRole, ROLES.ADMIN);
};

/**
 * Check if user is a superadmin
 * @param {string} userRole - Current user's role
 * @returns {boolean}
 */
export const isSuperAdmin = (userRole) => {
  return hasRole(userRole, ROLES.SUPERADMIN);
};

/**
 * Get user role display name
 * @param {string} userRole - Current user's role
 * @returns {string}
 */
export const getRoleDisplayName = (userRole) => {
  switch (userRole) {
    case ROLES.STUDENT:
      return 'Student';
    case ROLES.ADMIN:
      return 'Admin';
    case ROLES.SUPERADMIN:
      return 'Super Admin';
    default:
      return 'Unknown';
  }
};

/**
 * Get available roles for assignment (based on current user's role)
 * @param {string} currentUserRole - Current user's role
 * @returns {Array<string>}
 */
export const getAssignableRoles = (currentUserRole) => {
  if (isSuperAdmin(currentUserRole)) {
    return [ROLES.STUDENT, ROLES.ADMIN, ROLES.SUPERADMIN];
  } else if (isAdmin(currentUserRole)) {
    return [ROLES.STUDENT, ROLES.ADMIN];
  }
  return []; // Students can't assign roles
};

/**
 * Check if current user can assign a specific role
 * @param {string} currentUserRole - Current user's role
 * @param {string} roleToAssign - Role to be assigned
 * @returns {boolean}
 */
export const canAssignRole = (currentUserRole, roleToAssign) => {
  const assignableRoles = getAssignableRoles(currentUserRole);
  return assignableRoles.includes(roleToAssign);
};

// ============================================================================
// ORGANIZATION-SCOPED PERMISSION FUNCTIONS
// These functions handle permissions within the context of specific organizations
// ============================================================================

/**
 * Check if user has admin privileges within a specific organization
 * @param {string} userRole - Current user's role
 * @param {Array<string>} userOrganizationIds - Organizations user belongs to
 * @param {string} targetOrganizationId - Organization to check admin access for
 * @returns {boolean}
 */
export const isOrgAdmin = (userRole, userOrganizationIds = [], targetOrganizationId) => {
  // Superadmins have access to all organizations
  if (isSuperAdmin(userRole)) {
    return true;
  }
  
  // Regular admins only have access to their own organizations
  if (isAdmin(userRole)) {
    return userOrganizationIds.includes(targetOrganizationId);
  }
  
  return false;
};

/**
 * Check if current user can manage another user based on organization membership
 * @param {string} currentUserRole - Current user's role
 * @param {Array<string>} currentUserOrgIds - Current user's organization IDs
 * @param {Object} targetUser - Target user object with role and organizationIds
 * @returns {boolean}
 */
export const canManageUser = (currentUserRole, currentUserOrgIds = [], targetUser) => {
  if (!targetUser) return false;
  
  // Superadmins can manage anyone
  if (isSuperAdmin(currentUserRole)) {
    return true;
  }
  
  // Admins can manage users in their organizations (except other superadmins)
  if (isAdmin(currentUserRole)) {
    // Cannot manage superadmins
    if (isSuperAdmin(targetUser.role)) {
      return false;
    }
    
    // Must share at least one organization
    const targetUserOrgIds = targetUser.organizationIds || [];
    return currentUserOrgIds.some(orgId => targetUserOrgIds.includes(orgId));
  }
  
  // Students cannot manage other users
  return false;
};

/**
 * Get organizations where user has admin privileges
 * @param {string} userRole - Current user's role
 * @param {Array<string>} userOrganizationIds - User's organization IDs
 * @returns {Array<string>} - Organization IDs where user has admin access
 */
export const getAdminOrganizations = (userRole, userOrganizationIds = []) => {
  if (isSuperAdmin(userRole)) {
    // Superadmins have access to all organizations - return special flag
    return ['*']; // We'll handle this special case in components
  }
  
  if (isAdmin(userRole)) {
    return [...userOrganizationIds];
  }
  
  return [];
};

/**
 * Check if user can view organization data (admin-level access)
 * @param {string} userRole - Current user's role
 * @param {Array<string>} userOrganizationIds - User's organization IDs
 * @param {string} targetOrganizationId - Organization to check access for
 * @returns {boolean}
 */
export const canViewOrganization = (userRole, userOrganizationIds = [], targetOrganizationId) => {
  // Only admins and superadmins can view organization details
  if (isAdmin(userRole)) {
    // Superadmins can view all organizations
    if (isSuperAdmin(userRole)) {
      return true;
    }
    
    // Regular admins can only view their own organizations
    return userOrganizationIds.includes(targetOrganizationId);
  }
  
  // Students cannot view organization administrative data
  return false;
};

/**
 * Check if user can edit organization settings
 * @param {string} userRole - Current user's role
 * @param {Array<string>} userOrganizationIds - User's organization IDs
 * @param {string} targetOrganizationId - Organization to check edit access for
 * @returns {boolean}
 */
export const canEditOrganization = (userRole, userOrganizationIds = [], targetOrganizationId) => {
  return isOrgAdmin(userRole, userOrganizationIds, targetOrganizationId);
};

/**
 * Check if user can view basic organization info (name only, for UI context)
 * @param {string} userRole - Current user's role
 * @param {Array<string>} userOrganizationIds - User's organization IDs
 * @param {string} targetOrganizationId - Organization to check access for
 * @returns {boolean}
 */
export const canViewOrganizationBasicInfo = (userRole, userOrganizationIds = [], targetOrganizationId) => {
  // Users can see basic info (name) of organizations they belong to
  return userOrganizationIds.includes(targetOrganizationId);
};

/**
 * Filter users based on organization access permissions
 * @param {Array<Object>} users - Array of user objects
 * @param {string} currentUserRole - Current user's role
 * @param {Array<string>} currentUserOrgIds - Current user's organization IDs
 * @returns {Array<Object>} - Filtered array of users current user can see
 */
export const filterUsersByOrgAccess = (users, currentUserRole, currentUserOrgIds = []) => {
  if (!Array.isArray(users)) return [];
  
  // Superadmins can see all users
  if (isSuperAdmin(currentUserRole)) {
    return users;
  }
  
  // Admins can see users in their organizations
  if (isAdmin(currentUserRole)) {
    return users.filter(user => {
      const userOrgIds = user.organizationIds || [];
      return currentUserOrgIds.some(orgId => userOrgIds.includes(orgId));
    });
  }
  
  // Students can only see themselves (if included in the list)
  return [];
};

/**
 * Get role assignment permissions within organization context
 * @param {string} currentUserRole - Current user's role
 * @param {Array<string>} currentUserOrgIds - Current user's organization IDs
 * @param {string} targetOrganizationId - Organization context for role assignment
 * @returns {Array<string>} - Roles that can be assigned in this organization
 */
export const getOrgAssignableRoles = (currentUserRole, currentUserOrgIds = [], targetOrganizationId) => {
  // Must have admin access to the target organization
  if (!isOrgAdmin(currentUserRole, currentUserOrgIds, targetOrganizationId)) {
    return [];
  }
  
  // Return standard assignable roles based on user role
  return getAssignableRoles(currentUserRole);
};

/**
 * Check if user can assign a role within a specific organization
 * @param {string} currentUserRole - Current user's role
 * @param {Array<string>} currentUserOrgIds - Current user's organization IDs
 * @param {string} targetOrganizationId - Organization context
 * @param {string} roleToAssign - Role to be assigned
 * @returns {boolean}
 */
export const canAssignRoleInOrg = (currentUserRole, currentUserOrgIds = [], targetOrganizationId, roleToAssign) => {
  const assignableRoles = getOrgAssignableRoles(currentUserRole, currentUserOrgIds, targetOrganizationId);
  return assignableRoles.includes(roleToAssign);
};
